import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import CheckIcon from '@mui/icons-material/Check';
import { Checkbox, FormControl, FormControlLabel, Switch } from '@mui/material';
import React from 'react';
import { useCommonFormUiTranslation } from '../hooks';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, validateUniqueInList, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { type } from './types';
const WidgetFormCheckbox = (props) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const value = props.getDataBoolean();
    const formLabelSuffix = getFormLabelSuffix(props);
    const error = getVisibleError(props);
    const CheckboxOrSwitch = ((_a = props.config.style) === null || _a === void 0 ? void 0 : _a.type) === 'switch' ? Switch : Checkbox;
    const readOnly = props.config.readOnly || ((_b = props.rootParams.formParams) === null || _b === void 0 ? void 0 : _b.readOnly);
    const ariaDescribedBy = React.useId();
    const checkboxOrSwitchElement = (React.createElement(CheckboxOrSwitch, { checked: value, onChange: (event) => {
            if (readOnly) {
                return;
            }
            const newValue = event.target.checked;
            props.setDataBoolean(newValue);
            renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
        }, color: 'primary', disabled: props.config.disabled, inputProps: {
            tabIndex: props.editMode ? -1 : undefined,
            readOnly: readOnly,
            'aria-describedby': ariaDescribedBy,
        }, autoFocus: (_c = (!props.editMode && props.config.autoFocus)) !== null && _c !== void 0 ? _c : false, sx: readOnly ? { cursor: 'default' } : undefined }));
    let alignItems;
    switch ((_d = props.config.style) === null || _d === void 0 ? void 0 : _d.align) {
        case 'left':
            alignItems = 'flex-start';
            break;
        case 'center':
            alignItems = 'center';
            break;
        case 'right':
            alignItems = 'flex-end';
            break;
    }
    return (React.createElement(FormControl, { fullWidth: true, error: !!error, disabled: props.config.disabled, sx: { alignItems } },
        React.createElement(FormTooltip, { tooltip: props.config.tooltip },
            React.createElement(FormControlLabel, { sx: {
                    justifyContent: ((_e = props.config.style) === null || _e === void 0 ? void 0 : _e.labelPlacement) ? 'space-between' : undefined,
                    cursor: readOnly ? 'default' : 'pointer',
                }, control: checkboxOrSwitchElement, label: formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label, labelPlacement: (_g = (_f = props.config.style) === null || _f === void 0 ? void 0 : _f.labelPlacement) !== null && _g !== void 0 ? _g : undefined, disabled: props.config.disabled })),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormCheckboxEditor = (props) => {
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, {
            title: t('widget.widgetCheckbox.checkbox') + ': ' + props.config.label,
        }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            tooltip: true,
            readOnly: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.radio',
                    key: 'defaultValue',
                    label: t('widget.widgetForm.defaultValue'),
                    values: [
                        { label: t('common.null') },
                        { value: false, label: t('common.false') },
                        { value: true, label: t('common.true') },
                    ],
                },
            ],
        }), buildValidateTab(props, { required: true, uniqueInList: true }), buildHiddenTab(props, { t, allowDisable: true }), buildStyleTab(props, {
            align: true,
            children: [
                {
                    type: 'form.radio',
                    key: 'style.type',
                    label: t('widget.widgetCheckbox.checkboxStyle') + ':',
                    values: [
                        {
                            label: t('widget.widgetCheckbox.checkbox'),
                            value: 'checkbox',
                        },
                        {
                            label: t('widget.widgetSwitch.switch'),
                            value: 'switch',
                        },
                    ],
                    defaultValue: 'checkbox',
                },
                {
                    type: 'form.radio',
                    key: 'style.labelPlacement',
                    label: t('widget.widgetCheckbox.checkboxPlacement') + ':',
                    values: [
                        { label: t('widget.widgetTimePicker.start'), value: 'start' },
                        { label: t('validation.default'), value: undefined },
                    ],
                    defaultValue: undefined,
                },
            ],
        }), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormCheckbox, Object.assign({}, props))));
};
export const widgetFormCheckbox = {
    type,
    init: (props) => {
        if (props.getData() == null && props.config.defaultValue != null) {
            props.setDataBoolean(props.config.defaultValue);
        }
    },
    validate: (props) => {
        var _a, _b, _c, _d, _e;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataBoolean();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (!value) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        if (((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.uniqueInList) || ((_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.uniqueInGrid)) {
            const error = validateUniqueInList(props, value, props.dataPath);
            if (error) {
                return (_e = props.config.validate.uniqueInListMessage) !== null && _e !== void 0 ? _e : error;
            }
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        const value = props.getData();
        if (value != null) {
            return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), value ? 'Yes' : 'No');
        }
        else {
            return [];
        }
    },
    component: WidgetFormCheckbox,
    icon: React.createElement(CheckIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormCheckboxEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'checkbox', label: 'Checkbox' }),
};
